import { CHECK_IN_LABELS, CHECK_IN_MILESTONE_ORDER, ghostBtnClass, inputClass } from "./serviceShared";
import { useServiceEventCheckIns } from "./useServiceEventCheckIns";
import type { ServiceCheckInStatus } from "@/lib/service/types";

const EVIDENCE_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

type Props = {
  eventId: string;
  checkInStatus: ServiceCheckInStatus;
  onRecorded: () => void;
};

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function ServiceEventCheckInPanel({ eventId, checkInStatus, onRecorded }: Props) {
  const c = useServiceEventCheckIns(eventId, onRecorded);

  return (
    <div className="mt-4 border-t border-gray-100 pt-4 dark:border-zinc-800">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Check-in cadence</h3>
      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
        Current: {CHECK_IN_LABELS[checkInStatus] ?? checkInStatus}
      </p>

      {c.loading ? (
        <p className="mt-2 text-xs text-gray-500 dark:text-zinc-400">Loading check-in history…</p>
      ) : (
        <ol className="mt-3 space-y-2">
          {CHECK_IN_MILESTONE_ORDER.map((milestone) => {
            const row = c.latestByMilestone.get(milestone);
            const currentIdx =
              checkInStatus === "none"
                ? -1
                : CHECK_IN_MILESTONE_ORDER.indexOf(
                    checkInStatus as (typeof CHECK_IN_MILESTONE_ORDER)[number]
                  );
            const milestoneIdx = CHECK_IN_MILESTONE_ORDER.indexOf(milestone);
            const reached = milestoneIdx <= currentIdx;
            return (
              <li
                key={milestone}
                className={`flex flex-wrap items-start justify-between gap-2 rounded-lg border px-3 py-2 text-sm ${
                  reached
                    ? "border-indigo-200 bg-indigo-50/60 dark:border-indigo-800 dark:bg-indigo-950/30"
                    : "border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900"
                }`}
              >
                <div>
                  <div className="font-medium text-gray-900 dark:text-zinc-100">
                    {CHECK_IN_LABELS[milestone]}
                  </div>
                  {row ? (
                    <div className="mt-0.5 text-xs text-gray-600 dark:text-zinc-400">
                      {formatStamp(row.recorded_at)}
                      {row.notes ? ` · ${row.notes}` : ""}
                    </div>
                  ) : (
                    <div className="mt-0.5 text-xs text-gray-400 dark:text-zinc-500">Not recorded</div>
                  )}
                  {row?.evidence_url ? (
                    <a
                      href={row.evidence_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-xs text-indigo-700 underline dark:text-indigo-300"
                    >
                      View evidence
                    </a>
                  ) : null}
                </div>
                <button
                  type="button"
                  className={`${ghostBtnClass} text-xs`}
                  disabled={c.busy}
                  onClick={() => void c.recordMilestone(milestone)}
                >
                  Record
                </button>
              </li>
            );
          })}
        </ol>
      )}

      <div className="mt-3 space-y-2">
        <textarea
          className={`${inputClass} min-h-[4rem]`}
          placeholder="Notes for next check-in (optional)"
          value={c.notes}
          onChange={(e) => c.setNotes(e.target.value)}
          disabled={c.busy}
        />
        <div className="flex flex-wrap items-center gap-2">
          <label
            className={`${ghostBtnClass} text-xs inline-flex cursor-pointer items-center ${c.evidenceBusy ? "opacity-60 pointer-events-none" : ""}`}
          >
            {c.evidenceBusy ? "Uploading…" : c.pendingEvidenceUrl ? "Evidence attached" : "Attach photo"}
            <input
              type="file"
              accept={EVIDENCE_ACCEPT}
              capture="environment"
              className="hidden"
              disabled={c.evidenceBusy || c.busy}
              onChange={(ev) => {
                const file = ev.target.files?.[0];
                ev.target.value = "";
                if (file) void c.uploadEvidence(file);
              }}
            />
          </label>
          {c.pendingEvidenceUrl ? (
            <a
              href={c.pendingEvidenceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-indigo-700 underline dark:text-indigo-300"
            >
              Preview
            </a>
          ) : null}
        </div>
      </div>

      {c.error ? (
        <p className="mt-2 text-xs text-red-700 dark:text-red-300">{c.error}</p>
      ) : null}
    </div>
  );
}
